/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import * as React from 'react';
import { tokenSwaggerToTokenString } from 'src/core/handlers/azure/azureMappers';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getTokensService } from 'src/core/services/tokensService';

const DefaultPollTime = 5 * 60 * 1000;
const RefreshPollTimeOffset = 3 * 60 * 1000;

/**
 * Create a hook that controls refreshing of
 * the access tokens of the currently signed in
 * user without needing to refresh the entire page.
 */
export const useAuthRefresher = () => {
    const { rootStore } = useGlobalState();
    let userTokenTimeoutHandler: NodeJS.Timeout;

    /**
     * Ensure that the refresh queuing only runs when
     * the user is signed in.
     */
    React.useEffect(() => {
        if (rootStore.authStore.isSignedIn) {
            queueRefreshUserToken();
        } else {
            clearTimeout(userTokenTimeoutHandler);
        }
    }, [rootStore.authStore.isSignedIn]);

    /**
     * Triggers refresh of ARM tokens every poll interval.
     * The poll interval is extracted from the token itself.
     */
    const queueRefreshUserToken = () => {
        const { armToken } = rootStore.azureStore.session.userTokens;
        const pollTime = getPollTimeFromJwtToken(armToken);

        userTokenTimeoutHandler = setTimeout(async () => {
            await refreshAccessTokens();
            queueRefreshUserToken();
        }, pollTime);
    };

    const refreshAccessTokens = async () => {
        try {
            const tokensService = getTokensService();
            const { tenantId } = rootStore.azureStore.session;
            const { csAccountStore } = rootStore.azureStore.session;

            const [neutralArmToken, armToken, storageToken] = await Promise.all([
                tokensService.getNeutralArmToken().then(tokenSwaggerToTokenString),
                tokensService.getTenantArmToken({ tenantId }).then(tokenSwaggerToTokenString),
                tokensService.getAzureStorageAccountToken({ tenantId }).then(tokenSwaggerToTokenString)
            ]);

            if (csAccountStore) {
                const serviceKey = await getTokensService().getServiceKey({
                    tenantId,
                    armToken,
                    resourcePath: csAccountStore.path,
                    principalId: rootStore.userStore.principalId,
                    resourceKind: csAccountStore.kind
                });

                rootStore.azureStore.session.setServiceKey(serviceKey);
            }

            transaction(() => {
                rootStore.azureStore.session.setNeutralToken(neutralArmToken);
                rootStore.azureStore.session.setUserTokens({ armToken, storageToken });
            });
        } catch (error) {
            rootStore.azureStore.loadingState.setState('failed', error);
        }
    };
};

/**
 * Gets the next poll time as either the remaining
 * time from the token (JWT) or as the default
 * poll time if the token was not found or could
 * not be parsed.
 */
const getPollTimeFromJwtToken = (neutralToken: string) => {
    if (!neutralToken) {
        return DefaultPollTime;
    }

    let pollTime = DefaultPollTime;

    try {
        const tokenPayloadJsonString = atob(neutralToken.split('.')[1]);
        const tokenPayload = JSON.parse(tokenPayloadJsonString) as { exp: number };
        const tokenExpiryFromNow = tokenPayload.exp * 1000 - new Date().getTime();
        const tokenRefreshPollTime = Math.min(tokenExpiryFromNow - RefreshPollTimeOffset, tokenExpiryFromNow);

        if (!isNaN(tokenRefreshPollTime) && tokenRefreshPollTime > 0) {
            pollTime = tokenRefreshPollTime;
        }
    } catch {
        // Revert to default if token is invalid.
    }

    return pollTime;
};
